/**
 * sessionAuth.js — Sanctum SPA cookie auth (replaces logtoAuth.js).
 *
 * Uses native fetch with credentials:'include'. CSRF handshake via XSRF cookie.
 * No tokens stored in JS. No Authorization Bearer.
 *
 * Isolamento sessioni per-app: il backend emette un cookie XSRF per ruolo. Il
 * portale clienti è la app "user" → legge XSRF-TOKEN-user (header invariato
 * X-XSRF-TOKEN). Ref: ADR per-app session isolation — core config/spa.php.
 */

import { getRuntimeApiUrl } from './hostAwareConfig.js';

function apiUrl(path) {
  return getRuntimeApiUrl() + path;
}

function xsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN-user=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : '';
}

export async function ensureCsrf() {
  await fetch(apiUrl('/sanctum/csrf-cookie'), { credentials: 'include' });
}

export async function login(email, password, remember = false) {
  await ensureCsrf();
  const res = await fetch(apiUrl('/user/login'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-XSRF-TOKEN': xsrfToken(),
    },
    body: JSON.stringify({ email, password, remember }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.message || 'Login fallito');
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return res.json();
}

export async function logout() {
  await fetch(apiUrl('/user/logout'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-XSRF-TOKEN': xsrfToken(),
    },
  });
}

/**
 * Compat shim — l'autenticazione è verificata dallo store state, non da un token.
 * Questa funzione esiste per compatibilità con i caller che importavano logtoAuth.
 * @param {object} store - Vuex store (opzionale)
 */
export function getIsAuthenticated(store = null) {
  if (store) return store.getters.isAuthenticated;
  // Fuori dal contesto Vue — usa il cookie di sessione come proxy ottimistico.
  // Il vero check avviene via GET /user/user in tryLogin.
  return document.cookie.includes('laravel_session');
}

export async function forgotPassword(email) {
  await ensureCsrf();
  const res = await fetch(apiUrl('/user/forgot-password'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-XSRF-TOKEN': xsrfToken(),
    },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Richiesta fallita');
  }
  return res.json().catch(() => ({}));
}

export async function resetPassword(token, email, password, passwordConfirmation) {
  await ensureCsrf();
  const res = await fetch(apiUrl('/user/reset-password'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-XSRF-TOKEN': xsrfToken(),
    },
    body: JSON.stringify({
      token,
      email,
      password,
      password_confirmation: passwordConfirmation,
    }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.message || 'Reset fallito');
  }
  return res.json().catch(() => ({}));
}
